'use client';

import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EonSparkVersion, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { WarmCapacitySection } from './WarmCapacitySection';

import { eonSparkVersionLabelLong } from '../../../eonSparkVersionUtils';
import type { CreateSparkEngineWizardState } from '../createSparkEngineWizardState';

const EON_SPARK_VERSIONS = Object.values(EonSparkVersion);
const EON_SPARK_VERSION_SET = new Set<string>(EON_SPARK_VERSIONS);

const isEonSparkVersion = (value: string): value is EonSparkVersion =>
  EON_SPARK_VERSION_SET.has(value);

const roleArnConfig = getCloudFieldConfig(Provider.Aws, 'roleArn', {
  required: true,
});

const ECR_PATTERN =
  /^\d{12}\.dkr\.ecr\.[a-z0-9-]+\.amazonaws\.com\/[a-z0-9._/-]+:[a-zA-Z0-9._-]+$/;
const AR_PATTERN =
  /^[a-z0-9-]+-docker\.pkg\.dev\/[a-zA-Z0-9._-]+\/[a-zA-Z0-9._-]+\/[a-zA-Z0-9._/-]+:[a-zA-Z0-9._-]+$/;

function isValidCustomImage(url: string, cloud: string): boolean {
  if (!url) return true;
  const c = cloud.toLowerCase();
  if (c === 'aws') return ECR_PATTERN.test(url);
  if (c === 'gcp') return AR_PATTERN.test(url);
  return false;
}

const INSTANCE_FAMILIES_BY_CLOUD: Record<string, string[]> = {
  aws: ['m5', 'm6i', 'm7i', 'c5', 'c6i', 'c7i', 'r5', 'r6i', 'r7i'],
  gcp: ['n2-standard', 'n2-highmem', 'n2-highcpu', 'c2', 'e2'],
};

export const CreateSparkEngineStep = (
  props: StepProps<CreateSparkEngineWizardState>
) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<CreateSparkEngineWizardState>) =>
    setWizardState({ ...state, ...partial });

  const customImageValid = isValidCustomImage(
    state.customImage.trim(),
    state.workspaceCloud
  );

  const isAwsWorkspace = state.workspaceCloud.toLowerCase() === 'aws';

  const canCreate =
    state.name.trim().length >= 3 &&
    state.maxVcpu >= 1 &&
    state.maxMemory >= 1 &&
    state.maxStorage >= 1 &&
    customImageValid &&
    (!isAwsWorkspace ||
      roleArnConfig.validation.validateInput(state.iamRoleArn)) &&
    !creating;

  const instanceFamilies =
    INSTANCE_FAMILIES_BY_CLOUD[state.workspaceCloud.toLowerCase()] ?? [];

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      await dal.lakehouse.createSparkEngine(state.workspaceId, {
        name: state.name.trim(),
        eonSparkVersion: state.eonSparkVersion,
        maxVcpu: state.maxVcpu,
        maxMemory: state.maxMemory,
        maxStorage: state.maxStorage,
        warmCapacity: state.warmCapacityEnabled
          ? {
              vcpu: state.warmVcpu,
              memory: state.warmMemory,
              storage: state.warmStorage,
            }
          : undefined,
        concurrentJobs: state.concurrentJobs ?? undefined,
        queueTimeout: state.queueTimeout ?? undefined,
        instanceFamily: state.instanceFamily ?? undefined,
        dedicatedResources: state.dedicatedResources,
        ...(state.customImage.trim() && {
          customImage: state.customImage.trim(),
        }),
        ...(state.iamRoleArn.trim() && {
          awsRuntimeConfig: { iamRoleArn: state.iamRoleArn.trim() },
        }),
      });
      void props.finish('Spark engine created successfully');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create Spark engine'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create'}
      nextButtonLoadingText='Creating...'
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Set the engine name and Eon Spark version.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Name</Typography>
              </FormLabel>
              <TextField
                size='small'
                placeholder='Enter engine name'
                value={state.name}
                onChange={(e) => update({ name: e.target.value })}
                fullWidth
              />
            </FormControl>

            <FormControl fullWidth size='small'>
              <FormLabel>
                <Typography variant='subtitle1'>Eon Spark Version</Typography>
              </FormLabel>
              <Select
                size='small'
                value={state.eonSparkVersion}
                onChange={(e) => {
                  const v = e.target.value;
                  if (isEonSparkVersion(v)) update({ eonSparkVersion: v });
                }}
              >
                {EON_SPARK_VERSIONS.map((version) => (
                  <MenuItem key={version} value={version}>
                    {eonSparkVersionLabelLong(version)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Resource Limits */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Resource Limits</Typography>
              <Typography variant='body2' color='text.secondary'>
                Define the maximum compute, memory, and storage the engine can
                use.
              </Typography>
            </Stack>

            <Stack direction='row' gap='16px'>
              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>Max vCPU</Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  value={state.maxVcpu}
                  onChange={(e) =>
                    update({
                      maxVcpu: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>Max Memory (GiB)</Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  value={state.maxMemory}
                  onChange={(e) =>
                    update({
                      maxMemory: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>Max Storage (GB)</Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  value={state.maxStorage}
                  onChange={(e) =>
                    update({
                      maxStorage: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
        </Box>

        <WarmCapacitySection state={state} update={update} />

        {/* Job Limits */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Job Limits</Typography>
              <Typography variant='body2' color='text.secondary'>
                Control how many jobs run at the same time and how long queued
                jobs wait before timing out.
              </Typography>
            </Stack>

            <Stack direction='row' gap='16px'>
              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>Concurrent Jobs</Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  placeholder='Unlimited'
                  value={state.concurrentJobs ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    update({
                      concurrentJobs:
                        val === '' ? null : Math.max(1, parseInt(val) || 1),
                    });
                  }}
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>
                    Queue Timeout (minutes)
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  placeholder='No timeout'
                  value={state.queueTimeout ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    update({
                      queueTimeout:
                        val === '' ? null : Math.max(1, parseInt(val) || 1),
                    });
                  }}
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
        </Box>

        {/* Custom Image */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Custom Image</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify a custom container image for all Spark jobs on this
                engine.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>
                  Image URL (Optional)
                </Typography>
              </FormLabel>
              <TextField
                size='small'
                placeholder={
                  state.workspaceCloud.toLowerCase() === 'gcp'
                    ? 'us-docker.pkg.dev/my-project/repo/image:tag'
                    : '123456789012.dkr.ecr.us-east-1.amazonaws.com/repo:tag'
                }
                value={state.customImage}
                onChange={(e) => update({ customImage: e.target.value })}
                error={state.customImage.length > 0 && !customImageValid}
                fullWidth
              />
              {state.customImage.length > 0 && !customImageValid && (
                <FormHelperText error>
                  {state.workspaceCloud.toLowerCase() === 'gcp'
                    ? 'Invalid Artifact Registry URL format. Expected: <region>-docker.pkg.dev/<project>/<repo>/<image>:<tag>'
                    : 'Invalid ECR URL format. Expected: <account-id>.dkr.ecr.<region>.amazonaws.com/<repo>:<tag>'}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        </Box>

        {/* IAM Role (AWS only) */}
        {isAwsWorkspace && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>IAM Role</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Specify an IAM role for Spark workloads on this engine to use
                  at runtime.
                </Typography>
              </Stack>

              <FormTextField
                size='small'
                title='IAM Role ARN'
                placeholder={roleArnConfig.placeholder}
                validation={roleArnConfig.validation}
                value={state.iamRoleArn}
                onChange={(e) => update({ iamRoleArn: e.target.value })}
              />
            </Stack>
          </Box>
        )}

        {/* Advanced */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack
              direction='row'
              alignItems='center'
              gap='8px'
              sx={{ cursor: 'pointer', width: 'fit-content' }}
              onClick={() => update({ showAdvanced: !state.showAdvanced })}
            >
              <Typography variant='h6'>Advanced</Typography>
              <i
                className={
                  state.showAdvanced
                    ? 'ri-arrow-up-s-line'
                    : 'ri-arrow-down-s-line'
                }
              />
            </Stack>

            <Collapse in={state.showAdvanced}>
              <Stack gap='16px'>
                <FormControl size='small' sx={{ maxWidth: '260px' }}>
                  <FormLabel>
                    <Typography variant='subtitle1'>
                      Custom Instance Family
                    </Typography>
                  </FormLabel>
                  <Select
                    size='small'
                    value={state.instanceFamily ?? ''}
                    onChange={(e) =>
                      update({
                        instanceFamily:
                          e.target.value === '' ? null : e.target.value,
                      })
                    }
                  >
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {instanceFamilies.map((family) => (
                      <MenuItem key={family} value={family}>
                        {family}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.dedicatedResources}
                      onChange={(e) =>
                        update({ dedicatedResources: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography variant='body2'>
                      Do not share resources between this application and other
                      applications
                    </Typography>
                  }
                />
              </Stack>
            </Collapse>
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
